/**
 * `useNatsSubscription` · narrow-wire NATS subscription layer.
 *
 * Owns one JetStream consumer per tab whose filters span every
 * `jwt.allow_sub` root. When bounded subjects overlap (for example AMM mint +
 * wallet filters), the FilterManager partitions them into non-overlapping
 * consumer shards. Each consumer's `filter_subjects` array is mutated as the SDK's observer
 * trie gains/loses concrete subjects — so the wire only delivers events
 * the SDK actually has rendered interest in, NOT every event matching
 * the wildcard.
 *
 * Bootstrap never gives up while the client lives. One ROUND is four fast
 * attempts (1 / 2 / 4 s between them, each JetStream call bounded by the
 * 15 s manager timeout). If a round fails the registry goes REST-only (hooks
 * keep fetching and poll while the FSM reads DEGRADED_NO_NATS) and the
 * effect keeps starting new rounds on a jittered 5 → 60 s ladder until one
 * succeeds, the client changes, the client closes for good, or the effect
 * unmounts. Every failed attempt is reported through `onAttemptFailed`
 * (→ `reportNatsFailure`: console + telemetry + host hook).
 *
 * Speed: the JetStream chunk is preloaded while the socket connects
 * (`preloadJetStream`), and the tab's consumer is created with
 * the page's current filters (`initialInterest`) so the usual
 * placeholder → `consumers.update` round trip is skipped.
 *
 * Wire-interest plumbing: the `SubjectRegistry` fires `requestInterest` /
 * `releaseInterest` on 0↔1 transitions inside `registerObserver` /
 * `deregisterObserver`. `SDKProvider` installs the FilterManager's
 * `add` / `remove` callbacks on the registry singleton once bootstrap
 * succeeds; the FilterManager debounces (100 ms) and calls
 * `consumer.update({ filter_subjects: [...] })` once per shard per burst.
 *
 * Stream contract: all `morfi.*` subjects must be members of a single
 * JetStream stream (default name: `MORFI_EVENTS`). The stream is owned by
 * the publisher (sandbox + prod indexer) — not by this file.
 */
import { useEffect, useMemo, useRef } from 'react';
import { hasClosed } from '../nats/connect/intentional-close.js';
import { classifyFailureReason } from '../nats/connect/report-nats-failure.js';
import { clearPinnedFilterManager, FilterManager, pinFilterManager } from '../nats/wire/filter-manager.js';
import { installConsumerReleaseOnUnload } from '../nats/wire/release-on-unload.js';
/**
 * Name of the JetStream stream covering every `morfi.*` subject. The Morfi
 * stack's stream is `MORFI_EVENTS` (created + configured by the compose
 * `nats-bootstrap` service; the indexer publishes into it). Override via
 * env if a deployment names it differently — it's a deploy-time wire
 * detail, not a manifest concern.
 */
export const STREAM_NAME = typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_MORFI_NATS_STREAM || 'MORFI_EVENTS';
/** Fast attempts per bootstrap round, with `1_000 * 2 ** attempt` ms between them. */
export const BOOTSTRAP_FAST_ATTEMPTS = 4;
/** Background ladder between rounds: 5 s doubling to a 60 s cap, full jitter. */
export const BOOTSTRAP_ROUND_BASE_MS = 5_000;
export const BOOTSTRAP_ROUND_CAP_MS = 60_000;
/** Budget for a single JetStream API request during bootstrap. */
export const JETSTREAM_API_TIMEOUT_MS = 15_000;
let jetstreamModule = null;
/**
 * Load `@nats-io/jetstream` once. Dynamic import — it is a peer dep, and
 * bringing it in statically would bloat the bundle for consumers that don't
 * use realtime. The provider calls this while the socket is still
 * connecting so the chunk is cached by the time the client exists; a failed
 * load is forgotten so the next attempt retries it.
 */
export function preloadJetStream() {
  if (jetstreamModule === null) {
    jetstreamModule = import('@nats-io/jetstream').catch(err => {
      jetstreamModule = null;
      throw err;
    });
  }
  return jetstreamModule;
}
export class BootstrapCancelledError extends Error {
  constructor(where) {
    super(`FilterManager bootstrap cancelled (${where})`);
    this.name = 'BootstrapCancelledError';
  }
}
function sleepUnlessCancelled(ms, isCancelled) {
  return new Promise(resolve => {
    const tick = 250;
    let waited = 0;
    const step = () => {
      if (isCancelled()) {
        resolve();
        return;
      }
      if (waited >= ms) {
        resolve();
        return;
      }
      const next = Math.min(tick, ms - waited);
      waited += next;
      setTimeout(step, next);
    };
    step();
  });
}
/**
 * Open + bootstrap one `FilterManager` over the JWT's `allow_sub` roots —
 * one ROUND of `BOOTSTRAP_FAST_ATTEMPTS` attempts. Rethrows the last error
 * after the round so the caller's background loop can schedule the next.
 */
export async function openFilterManagerFor(client, allowSubRoots, onMessage, tabNonce, options = {}) {
  const isCancelled = options.isCancelled ?? (() => false);
  const round = options.round ?? 1;
  const offset = options.attemptOffset ?? 0;
  let lastErr;
  for (let i = 0; i < BOOTSTRAP_FAST_ATTEMPTS; i += 1) {
    // Bail BEFORE attempting init when the caller has signalled cancellation
    // (identity flip drained the old NC + opened a new one — this stale
    // bootstrap should not keep cycling against the dead client).
    //
    // NOTE — we deliberately do NOT short-circuit on `client.isClosed()`.
    // nats-core's `isClosed()` returns `true` during ordinary reconnect
    // windows (between transport teardown and re-establish); a bootstrap
    // that races a reconnect would be permanently aborted. A genuinely dead
    // client is recorded by the provider's `closed()` watcher (`hasClosed`).
    if (isCancelled()) throw new BootstrapCancelledError('superseded');
    if (hasClosed(client)) throw new BootstrapCancelledError('client closed');
    const attempt = offset + i + 1;
    let fm = null;
    try {
      const {
        jetstream,
        jetstreamManager
      } = await preloadJetStream();
      // `checkAPI: false` — skip the blocking `$JS.API.INFO` probe on
      // construction; it is exactly what times out at cold start on a slow
      // runtime. The real consumer ops below carry the budget instead.
      const jsm = await jetstreamManager(client, {
        checkAPI: false,
        timeout: JETSTREAM_API_TIMEOUT_MS
      });
      const js = jetstream(client);
      fm = new FilterManager({
        jsm,
        js,
        streamName: STREAM_NAME,
        roots: allowSubRoots,
        tabNonce,
        onMessage,
        isClientClosed: () => hasClosed(client),
        ...(options.onConsumerRecovered !== undefined ? {
          onConsumerRecovered: options.onConsumerRecovered
        } : {}),
        ...(options.replayFromAnchor !== undefined ? {
          replayFromAnchor: options.replayFromAnchor
        } : {})
      });
      fm.seedInterest(options.initialInterest ?? []);
      await fm.init();
      return fm;
    } catch (err) {
      lastErr = err;
      // Tear down so partially-created consumers + iterators are released
      // before the next attempt — otherwise a successful initOne in attempt N
      // leaves a server-side consumer behind that attempt N+1 collides with.
      if (fm !== null) {
        try {
          await fm.teardown();
        } catch {
          // Best-effort; inactive_threshold reaps anything that survives.
        }
      }
      if (isCancelled()) throw new BootstrapCancelledError('superseded mid-round');
      // The stream's consumer cap (JetStream 10026) is a broker-wide brake,
      // not a blip: fast retries would only add control-plane load at the
      // moment the cap exists to shed it. End the round and let the caller's
      // jittered round delay (up to a minute) pace the next try.
      const last = i === BOOTSTRAP_FAST_ATTEMPTS - 1 || isConsumerCapReached(err);
      const nextRetryMs = last ? options.afterRoundRetryMs ?? 0 : 1_000 * 2 ** i;
      options.onAttemptFailed?.({
        attempt,
        round,
        error: err,
        nextRetryMs
      });
      if (last) break;
      await sleepUnlessCancelled(nextRetryMs, isCancelled);
    }
  }
  throw lastErr;
}
function isConsumerCapReached(err) {
  return classifyFailureReason(err).reason === 'jetstream-api-10026';
}
/** Jittered delay before round `round + 1` (1-based rounds). */
export function bootstrapRoundDelayMs(round, randomFn = Math.random) {
  const base = Math.min(BOOTSTRAP_ROUND_CAP_MS, BOOTSTRAP_ROUND_BASE_MS * 2 ** Math.max(0, round - 1));
  return Math.floor(base / 2 + randomFn() * base / 2);
}
/**
 * React adapter — owns the FilterManager lifecycle tied to the provider's
 * NATS client + JWT allow_sub roots. The exposed callbacks are how
 * `SDKProvider` plumbs the trie into the wire.
 *
 * @param client     `NatsConnection` (may be `null` during connect handshake).
 * @param jwt        The current `JwtResponse` (anon → 5 JetStream roots, authed → 8).
 * @param onMessage  Callback invoked once per delivered event AFTER dedup.
 * @param tabNonce   Per-tab id baked into the consumer durable name.
 */
export function useNatsSubscription(client, jwt, onMessage, tabNonce, options = {}) {
  // Refs let the async loop read the latest callbacks without depending on
  // their identity. Written in post-commit `useEffect` so we don't mutate
  // refs during render (React-Compiler-clean).
  const onMessageRef = useRef(onMessage);
  const optionsRef = useRef(options);
  useEffect(() => {
    onMessageRef.current = onMessage;
    optionsRef.current = options;
  });
  // Value-stable snapshot. Effect re-fires only when allow_sub roots
  // actually change (login/logout, JWT refresh with different scope).
  const allowSubKey = jwt.allow_sub.join('|');
  const roots = useMemo(() => allowSubKey.split('|').filter(s => s.length > 0), [allowSubKey]);
  useEffect(() => {
    if (client === null) return;
    let cancelled = false;
    let manager = null;
    let disposeUnloadRelease = null;
    const isCancelled = () => cancelled;
    void (async () => {
      let round = 1;
      let attemptOffset = 0;
      while (!cancelled) {
        const afterRoundRetryMs = bootstrapRoundDelayMs(round, optionsRef.current.randomFn);
        try {
          const fm = await openFilterManagerFor(client, roots, (subject, data, seq) => onMessageRef.current(subject, data, seq), tabNonce, {
            initialInterest: optionsRef.current.getInitialInterest?.() ?? [],
            isCancelled,
            onAttemptFailed: failure => optionsRef.current.onAttemptFailed?.(failure),
            attemptOffset,
            round,
            afterRoundRetryMs,
            onConsumerRecovered: () => optionsRef.current.onConsumerRecovered?.()
          });
          if (cancelled) {
            void fm.teardown();
            return;
          }
          manager = fm;
          pinFilterManager(fm);
          const appState = optionsRef.current.appState;
          if (appState !== undefined) {
            disposeUnloadRelease = installConsumerReleaseOnUnload({
              appState,
              streamName: STREAM_NAME,
              getConsumerNames: () => fm.getConsumerNames(),
              publish: subject_0 => client.publish(subject_0)
            });
          }
          optionsRef.current.onReady?.(fm);
          return;
        } catch (err) {
          if (cancelled || err instanceof BootstrapCancelledError) return;
          // The socket is gone for good; the provider's closed() watcher
          // rebuilds the client, and that client gets its own effect.
          if (hasClosed(client)) return;
          if (round === 1) optionsRef.current.onBootstrapFailed?.({
            nextRetryMs: afterRoundRetryMs
          });
          attemptOffset += BOOTSTRAP_FAST_ATTEMPTS;
          round += 1;
          await sleepUnlessCancelled(afterRoundRetryMs, isCancelled);
        }
      }
    })();
    return () => {
      cancelled = true;
      clearPinnedFilterManager();
      disposeUnloadRelease?.();
      if (manager !== null) void manager.teardown();
    };
  }, [client, roots, tabNonce]);
}